/**
 * Training Program authorization checks for UX (UI visibility/hiding)
 *
 * IMPORTANT: Backend fully enforces authorization - these are UX checks only!
 *
 * Purpose: Hide unauthorized UI to improve user experience
 * - Shows/hides buttons, forms, sections based on role + obvious state
 * - Backend is the source of truth and will reject unauthorized actions
 * - Keep checks simple: only use role + data we already have in the frontend
 *
 * Philosophy: Aim to hide ~95% of unauthorized UI with simple checks.
 * The remaining ~5% that slips through is handled gracefully with backend
 * error responses (e.g., "Permission denied" toast messages).
 */

import type {AuthUser} from '@/matic/types/auth';
import type {TrainingProgram} from '../types/training-program';

/**
 * Can user create training programs?
 *
 * Simple role check - only Admin and Quality can create.
 * Backend enforces full business rules.
 */
export function canCreateTrainingProgram(user: AuthUser): boolean {
  return user.role === 'admin' || user.role === 'quality';
}

/**
 * Can user edit this training program?
 *
 * Checks:
 * - Must be DRAFT status
 * - Role (admin/quality only)
 *
 * Backend also checks: additional business rules, locks, etc.
 */
export function canEditTrainingProgram(
  user: AuthUser,
  program: TrainingProgram
): boolean {
  // Must be draft to edit
  if (program.status !== 'draft') {
    return false;
  }

  // Admin and quality can edit
  return user.role === 'admin' || user.role === 'quality';
}

/**
 * Can user delete this training program?
 *
 * Checks:
 * - Must be DRAFT status
 * - Role (admin/quality only)
 *
 * Backend enforces: prevents deletion if dependencies exist
 */
export function canDeleteTrainingProgram(
  user: AuthUser,
  program: TrainingProgram
): boolean {
  // Must be draft to delete
  if (program.status !== 'draft') {
    return false;
  }

  // Admin and quality can delete
  return user.role === 'admin' || user.role === 'quality';
}

/**
 * Can user publish a training program revision?
 *
 * Simple role check - only Admin and Quality can publish.
 */
export function canPublishTrainingProgram(user: AuthUser): boolean {
  return user.role === 'admin' || user.role === 'quality';
}

/**
 * Can user retire this training program?
 *
 * Checks:
 * - Must be PUBLISHED status
 * - Role (admin/quality only)
 */
export function canRetireTrainingProgram(
  user: AuthUser,
  program: TrainingProgram
): boolean {
  // Must be published to retire
  if (program.status !== 'published') {
    return false;
  }

  // Admin and quality can retire
  return user.role === 'admin' || user.role === 'quality';
}

/**
 * Can user view this training program?
 *
 * All authenticated users can view programs.
 * Backend may restrict based on tenant/org boundaries.
 */
export function canViewTrainingProgram(user: AuthUser): boolean {
  // All authenticated users can view
  return user !== null && user !== undefined;
}

/**
 * See comment regarding: KNOWN ISSUE - Access control inconsistency
 * Can user access the Training Programs management section?
 *
 * Only Admin and Quality roles can access training program management.
 * Normal and Basic users only see "My Training" (their assignments).
 *
 * This permission is used by:
 * - TrainingProgramLayout (route guard)
 * - Sidebar navigation (showing/hiding "Training Programs" link)
 */
export function canAccessTrainingPrograms(user: AuthUser): boolean {
  return user.role === 'admin' || user.role === 'quality';
}
